import React, { useState, useEffect } from 'react';
import Cookie from 'js-cookie';
import PropTypes from 'prop-types';
import { T, t, defineMessages } from '@sm/intl';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';

import {
  Align,
  Button,
  Col,
  Container,
  Grid,
  Modal,
  Row,
  Sheet,
  Typography,
} from '@sm/wds-react';

import { TabletScreen, MobileScreen } from '../MediaComponents';

import cookieImage from './static/images/cookies-plate.png';

import './cookie-banner.scss';

const CONSENT_KEY = 'gdpr_consent';
const CONSENT_DENIED_KEY = 'gdpr_consent_denied';

const COPY = defineMessages({
  COOKIE_BANNER: {
    id: 'CookieBanner.cookiePolicy',
    defaultMessage:
      // eslint-disable-next-line no-multi-str
      'In order to give you the best experience, SurveyMonkey and our third party partners may use cookies\
    and similar technologies, for example, to analyze usage and optimize our sites and services, personalize content,\
    tailor and measure our marketing and to keep the site secure. Please visit our\
    <a href="/mp/legal/privacy-policy/">privacy policy</a> for more information and our\
    <a href="/mp/legal/cookies/">cookies policy</a> for a list of all cookies used.',
    description: '[Type: Label][Vis.: med] - Cookie Banner Message',
  },
  COOKIE_PREFERENCES: {
    id: 'CookieBanner.preferences',
    defaultMessage:
      '<a href="/mp/legal/cookies/">Clear or manage cookie preferences</a>.',
    description: '[Type: Label][Vis.: med] - Cookie Banner Preferences',
  },
  IMAGE_ALT: {
    id: 'CookieBanner.imageAlt',
    defaultMessage: 'Plate of Cookies',
    description: '[Type: Label][Vis.: med] - Cookie Banner Image Alt Tag',
  },
  NO: {
    id: 'CookieBanner.decline',
    defaultMessage: 'DISAGREE',
    description: '[Type: Label][Vis.: med] - Cookie Banner Decline button',
  },
  AGREE: {
    id: 'CookieBanner.agree',
    defaultMessage: 'AGREE',
    description: '[Type: Label][Vis.: med] - Cookie Banner Agree button',
  },
});

const CookieBanner = ({ GDPR }) => {
  const { hasGDPRConsent, hasExplictlyDenied } = GDPR;
  const [show, setShow] = useState(true);
  const [consent, setConsent] = useState('gdpr_consent_denied');

  useEffect(() => {
    if (!show) {
      MetricsTracker.track({
        name: USER_EVENTS.GDPR_ACTION,
        data: {
          actionType: USER_EVENTS.GDPR_ACTION,
          actionFlow: consent !== CONSENT_KEY ? 'denyConsent' : 'giveConsent',
        },
      });
    }
  }, [show, consent]);

  if (hasGDPRConsent || hasExplictlyDenied || !show) {
    return null;
  }

  /**
   * Agree to tracking cookie
   * @param {Object} e
   */
  const onAgree = e => {
    e.stopPropagation();
    Cookie.set(CONSENT_KEY, 'true', { expires: 365, secure: true });
    setConsent(CONSENT_KEY);
    setShow(!show);
  };

  /**
   * Deny tracking cookie
   */
  const onDeny = () => {
    Cookie.set(CONSENT_DENIED_KEY, 'true', { expires: 7, secure: true });
    setConsent(CONSENT_DENIED_KEY);
    setShow(!show);
  };

  return (
    <div className="sm-cookie-banner" data-testid="CookieBanner">
      <TabletScreen>
        <Modal
          closeButtonClass="gdpr-cta-no"
          size="md"
          show={show}
          onClose={onDeny}
          ariaLabel={t(COPY.NO)}
          data-testid="CookieBanner__Modal"
        >
          <Grid className="sm-cookie-banner__grid" noBleeds noGutters>
            <Row>
              <Col className="sm-cookie-banner__col" md={3}>
                <Container
                  className="sm-cookie-banner__img-wrapper"
                  flex
                  flexDirection="column"
                  flexJustify="center"
                  flexAlign="center"
                >
                  <img
                    src={cookieImage}
                    className="sm-cookie-banner__img"
                    alt={t(COPY.IMAGE_ALT)}
                  />
                </Container>
              </Col>
              <Col md={9}>
                <Container p="5" className="sm-cookie-banner__modal-body">
                  <Typography
                    className="sm-cookie-banner__text-container"
                    component="div"
                    variant="body"
                    color="dark-muted"
                  >
                    <T desc={COPY.COOKIE_BANNER} html />
                  </Typography>
                  <Typography
                    className="sm-cookie-banner__link"
                    color="dark-muted"
                    component="div"
                    variant="body"
                  >
                    <T desc={COPY.COOKIE_PREFERENCES} html />
                  </Typography>
                </Container>
                <Container
                  className="sm-cookie-banner__cta-container"
                  pr="4"
                  pb="4"
                >
                  <Align placement="right">
                    <Button
                      className="sm-cookie-banner__cta-btn gdpr-cta-agree"
                      onClick={onAgree}
                      data-testid="CookieBanner__Agree"
                    >
                      <T desc={COPY.AGREE} />
                    </Button>
                  </Align>
                </Container>
              </Col>
            </Row>
          </Grid>
        </Modal>
      </TabletScreen>
      <MobileScreen>
        <Sheet
          placement="bottom"
          closeButtonClass="gdpr-cta-no"
          onClose={onDeny}
          show={show}
          ariaLabel={t(COPY.NO)}
          closeButton
        >
          <Container pt="6" pb="5" px="7">
            <Typography
              className="sm-cookie-banner__text-container"
              component="div"
              variant="body"
              color="dark-muted"
            >
              <T desc={COPY.COOKIE_BANNER} html />
            </Typography>
            <Typography
              className="sm-cookie-banner__link"
              color="dark-muted"
              component="div"
              variant="body"
            >
              <T desc={COPY.COOKIE_PREFERENCES} html />
            </Typography>
            <Container pb="3" pt="3">
              <Align placement="left">
                <Button
                  className="sm-cookie-banner__cta-btn gdpr-cta-agree"
                  onClick={onAgree}
                  data-testid="CookieBanner__Agree"
                >
                  <T desc={COPY.AGREE} />
                </Button>
              </Align>
            </Container>
          </Container>
        </Sheet>
      </MobileScreen>
    </div>
  );
};

CookieBanner.propTypes = {
  GDPR: PropTypes.shape({
    hasGDPRConsent: PropTypes.bool,
    hasExplictlyDenied: PropTypes.bool,
  }).isRequired,
};

export default CookieBanner;
